/**
 * BountyHunterModule.ts — the hired stalker, rendered (2026-08-30; the
 * STALKING REWORK 2026-09-18).
 *
 * The server (lib/bounty_hunt) is the DIRECTOR and, since the rework, the
 * BODY too: the hunter has a real world position, a belief about the mark,
 * an opening with hysteresis, a phase machine (track → observe → shadow →
 * stage → commit → engage → break → search → withdraw), and its blows are
 * npc_strike windups scheduled from its own position. This module RENDERS
 * the `bounty_hunt` projection (phase, materialized, x/y/z/yaw), interpolating
 * a body toward the server's transform — turning around never moves it —
 * and PRESENTS the evidence:
 *
 *   · RECOGNITION is a pipeline, not a spawn: frustum → projected size →
 *     occlusion → clear-view dwell → recognised; identified only up close,
 *     in combat, or when the mark strikes first. Spawning is not a sighting.
 *   · EVIDENCE has four levels (ambient / suspicious / corroborated /
 *     confirmed). Nothing in the HUD names the hunter before CONFIRMED; a
 *     faint "possibly followed" appears at CORROBORATED (two independent
 *     cues, or a recognised figure). No plate, no eye, no sting on spawn.
 *   · CUES (`bounty_cue` rows) are played as ordinary world sounds with a
 *     quiet CAPTION that describes the evidence ("Footsteps, behind left"),
 *     never the answer. Bespoke cue sounds fall back to existing clips
 *     until they are generated.
 *   · The ship ESCAPE (2026-09-07) stays as pure choreography, triggered by
 *     the server's `withdraw` phase while the body is near.
 *   · The mark's blows still go through WildsModule's ONE attack path via the
 *     ground_targets entry; the server refuses a strike with no body in reach.
 *
 * Air (skyFlight / hoverRide) keeps the legacy hull on the air-target
 * registry — the server suspends the agent in that domain (its own
 * migration gate); space is SpaceModule's. Personal worlds are sanctuary on
 * BOTH sides now: the server suspends the agent and clears the opening.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase, Sub } from '../engine-compat.js';
import { groundYIn } from '../lib/grounding.js';
import { prefabAssetId } from '../lib/player_models.js';
import { markShared, deferDisposeTree } from '../lib/defer_dispose.js';
import { settlementSites } from '../../../shared/settlements.js';
import { sfx, sfxAt, sfxLater, sfxLoop, sfxAvailable } from '../lib/sfx.js';
import { reportThreat } from '../lib/audio/threat.js';
import { makePlate, type Plate } from './living-events/HealthBars.js';
import { upsertAirTarget, airTargetById, clearAirTargetsByPrefix } from '../lib/air_targets.js';
import { sunElevation } from '../lib/lighting.js';
import { structureHitAt } from '../lib/structure_solids.js';
import { windups } from '../lib/combat_telegraph.js';
import { nameFromTables } from '../lib/versona_names.js';
import {
  weaknessScore, BOUNTY_TIERS, SHADOW_FLEE_M,
  escapeShipSpot, escapeFlight, ESCAPE_BOARD_M, ESCAPE_BOARD_S, ESCAPE_FLIGHT_S, ESCAPE_RUN_MULT,
  forwardBearing, recognitionNeededS, projectedHeightPx, RECOGNIZE_MIN_PX, IDENTIFY_RANGE_M, CORROBORATE_WINDOW_S,
  EVIDENCE_AMBIENT, EVIDENCE_SUSPICIOUS, EVIDENCE_CORROBORATED, EVIDENCE_CONFIRMED, SHELTER_TTL_S,
  type WeaknessMetrics, type BountyTier, type HuntPhase, type CueKind,
} from '../lib/bounty_logic.js';
import { upsertGroundTarget, removeGroundTarget } from '../lib/ground_targets.js';
import type { AssetModule } from './AssetModule.js';

interface BountyRow {
  bountyId: bigint;
  target: { isEqual(o: unknown): boolean };
  contractorKind: string;
  contractorLabel: string;
  spend: bigint;
  tier: number;
  hunterName: string;
  hunterHp: number;
  hunterMaxHp: number;
  state: string;
  placedAt: { microsSinceUnixEpoch: bigint };
}
interface HuntRow {
  bountyId: bigint;
  target: { isEqual(o: unknown): boolean };
  phase: string;
  materialized: boolean;
  x: number; y: number; z: number; yaw: number;
  revision: number;
  domainEpoch: number;
}
interface CueRow {
  cueId: bigint;
  target: { isEqual(o: unknown): boolean };
  kind: string;
  x: number; z: number;
  refId: bigint;
  variant: number;
  createdAt: { microsSinceUnixEpoch: bigint };
  expiresAt: { microsSinceUnixEpoch: bigint };
}
interface EventRow {
  eventId: bigint;
  bountyId: bigint;
  target: { isEqual(o: unknown): boolean };
  kind: string;
  detail: string;
  at: { microsSinceUnixEpoch: bigint };
}
interface VitalsRow { hp: number; maxHp: number; lastHurtAt: { microsSinceUnixEpoch: bigint } }
interface CreatureRow { state: string; targetKind: string; targetPlayer: { isEqual(o: unknown): boolean } }
interface EquipRow { owner: { isEqual(o: unknown): boolean }; slot: string; itemKey: string }
interface SkyRow { timeOfDay: number }
interface WorldRow { worldId: bigint; kind: string; terrainSeed: string }
interface StrikeRow { attackerKey: string; targetPlayer: { isEqual(o: unknown): boolean }; windupMs: number; x: number; z: number }

const CHASE_SPEED = 6.4;        // m/s on foot — the escape run scales from it
const AIR_CHASE_SPEED = 74;     // m/s in the sky
const HIT_RANGE_M = 2.3;
const AIR_FIRE_RANGE_M = 520;
const AIR_FIRE_GAP_S = 1.4;
const SHOOT_CD_S = 0.35;        // the war-air bolt report cadence
/** The rendered body follows the server transform at this rate (1/s); a jump past SNAP_M snaps. */
const FOLLOW_RATE = 4;
const SNAP_M = 14;
/** Occlusion is sampled at this cadence (frames), never every frame. */
const OCCLUSION_EVERY = 6;
/** The escape choreography starts when the withdrawing body is inside this range (m). */
const ESCAPE_NEAR_M = 70;
/** The shelter report cadence while indoors (s) — under the server's TTL. */
const SHELTER_REPORT_S = Math.max(2, SHELTER_TTL_S - 4);
/** Caption hold (ms). */
const CAPTION_MS = 4200;

/** Original sample writing, conditioned on a witness that actually stood there. */
const WITNESS_LINES = [
  'That pilot asked when the eastern gate closes.',
  'Someone left before their drink arrived.',
  'I thought that was your ship behind us.',
  'The door was open a moment ago.',
];

export class BountyHunterModule implements IModule {
  readonly name = 'atlas-bounty-hunter';
  private app: App | null = null;
  private subbed = false;

  // Hunter visuals
  private bodyMaster: THREE.Object3D | null = null;
  private shipMaster: THREE.Object3D | null = null;
  private loadTried = false;
  private body: THREE.Group | null = null;      // ground stalker (clone + eye)
  private eye: THREE.Mesh | null = null;
  private ring: THREE.Mesh | null = null;       // the telegraph ring under a winding-up hunter
  private ringGeo: THREE.RingGeometry | null = null;
  private ringMat: THREE.MeshBasicMaterial | null = null;
  private ship: THREE.Group | null = null;      // air hull
  private plate: Plate | null = null;
  private hx = 0; private hy = 0; private hz = 0; // rendered hunter position (world)
  private hyaw = 0;
  private visible = false;
  private lastRevision = -1;
  private lastPhase = '';
  /** The escape in progress: the run to the hatch, the boarding pause, the climb-out. */
  private escape: {
    phase: 'run' | 'board' | 'lift'; t: number;
    sx: number; sy: number; sz: number; yaw: number; hatchX: number; hatchZ: number; awayX: number; awayZ: number;
  } | null = null;
  private escapes = 0;
  private escapeStart = { hx: 0, hz: 0, sy: 0, d: 0 };
  /** The client's OWN record of the last escape's cooldown (diagnostic — the server's next opportunity is the authority). */
  private vanishUntil = 0;
  private lastEscape: { phases: string[]; hunterRunM: number; climbM: number; recedeM: number; flightS: number; endedBy: string } | null = null;
  private escapeTimeScale = 1;
  private escapedRevision = -1;

  // Evidence
  private evidence = EVIDENCE_AMBIENT;
  private identified = false;
  private clearS = 0;
  private occluded = false;
  private occlusionFrame = 0;
  private cueLog: Array<{ kind: string; atS: number }> = [];
  private seenCues = new Set<string>();
  private firstCueScan = true;
  private caption: HTMLDivElement | null = null;
  private captionUntil = 0;
  private commitRevision = -1;
  private strikeSubbed = false;

  // Dread + prediction (the score is a local PREDICTION for diagnostics only — the server decides)
  private seenEvents = new Set<string>();
  private firstEventScan = true;
  private evalAcc = 0;
  private hitAcc = 0;
  private shootCd = 0;
  private lastPushedHp = 0;
  private lastShipDamageAt = 0;
  private prevX = 0; private prevZ = 0; private speedMps = 0;
  private droneOn = false;
  private droneGain = 0;
  private hud: HTMLDivElement | null = null;
  private clockS = 0;
  private lastScore = 0;
  private shelterAcc = 0;
  private shelterWas = false;
  /** The probe lever's wish: keep asking the server for a body until this clock. */
  private showWantUntil = -1;
  private showAskedAt = -1e9;

  constructor(private assets: AssetModule) {}

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('BountyHunter', Phase.Animation, 44, (dt) => this.frame(dt), Sub.Update));
    bus.on('atlas:ship-damage', () => { this.lastShipDamageAt = this.clockS; });
    bus.on('atlas:bounty-evidence', ({ level, why }) => this.raiseEvidence(level, why));
    (globalThis as Record<string, unknown>).__bounty = () => {
      const b = this.contract();
      const h = b ? this.hunt(b) : null;
      return {
        contract: b ? { id: b.bountyId.toString(), state: b.state, hunter: b.hunterName, hp: b.hunterHp, tier: b.tier } : null,
        hunt: h ? { phase: h.phase, materialized: h.materialized, at: [+h.x.toFixed(1), +h.y.toFixed(1), +h.z.toFixed(1)], yaw: +h.yaw.toFixed(2), revision: h.revision, domainEpoch: h.domainEpoch } : null,
        visible: this.visible, score: this.lastScore,
        evidence: this.evidence, identified: this.identified, sighted: this.identified, clearS: +this.clearS.toFixed(2), occluded: this.occluded,
        cues: this.cueLog.length, lastCue: this.cueLog[this.cueLog.length - 1] ?? null,
        escape: this.escape ? { phase: this.escape.phase, t: +this.escape.t.toFixed(2), ship: this.ship ? [+this.ship.position.x.toFixed(1), +this.ship.position.y.toFixed(1), +this.ship.position.z.toFixed(1)] : null } : null,
        escapes: this.escapes, vanishLeftS: Math.max(0, +(this.vanishUntil - this.clockS).toFixed(1)), lastEscape: this.lastEscape,
        hunterAt: this.body ? [+this.hx.toFixed(1), +this.hy.toFixed(1), +this.hz.toFixed(1)] : null,
        hunterDist: this.body ? +Math.hypot(this.hx - atlas.localX, this.hz - atlas.localZ).toFixed(1) : null,
        drone: this.droneGain,
      };
    };
    // Probe levers: ask the SERVER to materialize the body now (the real vantage pick),
    // and run the escape choreography from wherever the body stands (the server is told to withdraw).
    (globalThis as Record<string, unknown>).__bountyShow = () => {
      const b = this.contract();
      const conn = atlas.conn;
      if (!b || b.state !== 'stalking' || !conn || this.escape) return false;
      if (this.body) return true;
      // Ask until a body stands (a resume-reload suspends the agent; the frame re-asks every 5 s for a minute).
      this.showWantUntil = this.clockS + 60;
      this.showAskedAt = -1e9;
      return true;
    };
    (globalThis as Record<string, unknown>).__bountyEscape = (timeScale = 1) => {
      const b = this.contract();
      const conn = atlas.conn;
      if (!b || this.escape || !conn || !this.body) return false;
      this.escapeTimeScale = Math.max(1, Math.min(20, Number(timeScale) || 1));
      void (conn.reducers as unknown as { debugBountyNudge(a: { bountyId: bigint; op: string }): Promise<void> })
        .debugBountyNudge({ bountyId: b.bountyId, op: 'withdraw' }).catch(() => { /* gated */ });
      this.escapedRevision = this.hunt(b)?.revision ?? -1;
      this.beginEscape(conn, b, atlas.localX, atlas.localZ);
      return true;
    };
  }

  // ── Rows ────────────────────────────────────────────────────────────────
  private ensureSub(): void {
    const conn = atlas.conn;
    if (this.subbed || !conn) return;
    this.subbed = true;
    try {
      conn.subscriptionBuilder().subscribe([
        'SELECT * FROM bounty_contract WHERE target = :sender',
        'SELECT * FROM bounty_contract WHERE placed_by = :sender',
        'SELECT * FROM bounty_event WHERE target = :sender',
        'SELECT * FROM bounty_hunt WHERE target = :sender',
        'SELECT * FROM bounty_cue WHERE target = :sender',
      ]);
    } catch { this.subbed = false; }
  }

  /** The one LIVE contract on me (the max-one-per-target law). */
  private contract(): BountyRow | null {
    const conn = atlas.conn;
    if (!conn) return null;
    try {
      for (const b of (conn.db as unknown as { bountyContract: { iter(): Iterable<BountyRow> } }).bountyContract.iter()) {
        if ((b.state === 'stalking' || b.state === 'attacking') && b.target.isEqual(conn.identity)) return b;
      }
    } catch { /* not subscribed yet */ }
    return null;
  }

  /** The server's projection of the encounter for a contract. */
  private hunt(b: BountyRow): HuntRow | null {
    const conn = atlas.conn;
    if (!conn) return null;
    try {
      const t = (conn.db as unknown as { bountyHunt?: { bountyId: { find(id: bigint): HuntRow | undefined } } }).bountyHunt;
      return t?.bountyId.find(b.bountyId) ?? null;
    } catch { return null; }
  }

  private tierOf(b: BountyRow): BountyTier {
    return BOUNTY_TIERS[Math.max(0, Math.min(BOUNTY_TIERS.length - 1, b.tier - 1))];
  }

  private worldRow(): WorldRow | null {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return null;
    try { return (conn.db as unknown as { world: { worldId: { find(id: bigint): WorldRow | undefined } } }).world.worldId.find(wid) ?? null; } catch { return null; }
  }

  // ── Frame ───────────────────────────────────────────────────────────────
  private frame(dt: number): void {
    this.clockS += dt;
    const conn = atlas.conn;
    if (!conn) return;
    this.ensureSub();
    this.ensureStrikeHooks(conn);
    this.surfaceEvents(conn);
    this.surfaceCues(conn);
    this.tickCaption();

    const b = this.contract();
    if (!b) {
      if (this.escape) this.endEscape('teardown:no-contract');
      this.teardownHunter(); this.setHud(null);
      this.evidence = EVIDENCE_AMBIENT; this.identified = false; this.clearS = 0; this.cueLog.length = 0;
      return;
    }

    const world = this.worldRow();
    const sanctuary = world?.kind === 'personal';
    const inSpace = atlas.spaceFlight; // the space sector renders its own hunter (SpaceModule)
    if (sanctuary || inSpace) { if (this.escape) this.endEscape(sanctuary ? 'teardown:sanctuary' : 'teardown:space'); this.teardownHunter(); }

    this.reportShelter(conn, dt);
    this.updateHud(b);

    // The local prediction of the score, 1 Hz — diagnostics only (the server owns the decision).
    this.evalAcc += dt;
    if (this.evalAcc >= 1) { this.evalAcc = 0; this.predictWeakness(conn, b, world); }

    if (sanctuary || inSpace) return;
    this.ensureMasters(conn);

    const px = atlas.localX, pz = atlas.localZ;
    this.speedMps = dt > 0 ? Math.hypot(px - this.prevX, pz - this.prevZ) / Math.max(dt, 1 / 240) : 0;
    this.prevX = px; this.prevZ = pz;

    const flying = atlas.skyFlight || atlas.hoverRide;
    if (flying && b.state === 'attacking') { this.airFrame(conn, b, dt); return; }
    clearAirTargetsByPrefix('bounty:');
    if (this.ship && !this.escape) this.dropShip();

    // An escape in flight owns the body and the ship until the hull is gone.
    if (this.escape) {
      if (flying || atlas.interiorId || atlas.submerged) { this.endEscape(flying ? 'flying' : atlas.interiorId ? 'interior' : 'submerged'); return; }
      if (b.state !== 'stalking') this.endEscape('state:' + b.state);
      else { this.escapeFrame(conn, b, dt); return; }
    }

    const h = this.hunt(b);
    if (this.clockS < this.showWantUntil && (!h || !h.materialized) && this.clockS - this.showAskedAt > 5) {
      this.showAskedAt = this.clockS;
      void (conn.reducers as unknown as { debugBountyNudge(a: { bountyId: bigint; op: string }): Promise<void> })
        .debugBountyNudge({ bountyId: b.bountyId, op: 'materialize' }).catch(() => { /* gated */ });
    }
    if (!h || !h.materialized || h.phase === 'suspended') { this.hideBody(); this.setDrone(0); return; }
    if (h.materialized) this.showWantUntil = -1;
    this.groundFrame(conn, b, h, dt);
  }

  // ── The body follows the server (ground) ───────────────────────────────
  private groundFrame(conn: NonNullable<typeof atlas.conn>, b: BountyRow, h: HuntRow, dt: number): void {
    const px = atlas.localX, pz = atlas.localZ;
    const fresh = !this.body;
    if (fresh) {
      // An availability ask only STARTS a load: warm the hunt's own clips now, a minute before its first cue, or the first
      // footstep / carrier / commit tell of every session plays its stand-in.
      for (const k of ['sfx_hunter_step', 'sfx_hunter_harness', 'sfx_hunter_radio', 'sfx_hunter_ready']) void sfxAvailable(k);
      this.hx = h.x; this.hz = h.z; this.hyaw = h.yaw;
      this.hy = groundYIn(conn, atlas.currentWorldId, this.hx, this.hz);
      if (!this.showBody()) return;
      this.visible = true;
      this.clearS = 0;
    }
    // Follow: ease toward the server transform; a jump past SNAP_M is a re-seat, not a walk.
    const gap = Math.hypot(h.x - this.hx, h.z - this.hz);
    if (gap > SNAP_M) { this.hx = h.x; this.hz = h.z; }
    else { const k = Math.min(1, dt * FOLLOW_RATE); this.hx += (h.x - this.hx) * k; this.hz += (h.z - this.hz) * k; }
    this.hy = groundYIn(conn, atlas.currentWorldId, this.hx, this.hz);
    const phase = h.phase as HuntPhase;
    const wantYaw = phase === 'engage' || phase === 'commit' ? Math.atan2(px - this.hx, pz - this.hz) : h.yaw;
    let dy = wantYaw - this.hyaw;
    while (dy > Math.PI) dy -= Math.PI * 2;
    while (dy < -Math.PI) dy += Math.PI * 2;
    this.hyaw += dy * Math.min(1, dt * 5);
    this.poseBody(b, phase);

    // Phase transitions the presentation reacts to (once per revision).
    if (h.revision !== this.lastRevision || phase !== this.lastPhase) {
      if (phase === 'commit' && this.commitRevision !== h.revision) {
        this.commitRevision = h.revision;
        // The readable tell: a weapon readied, close. Clear and unrecycled.
        this.playCueSound('sfx_hunter_ready', 'sfx_equip', 0.55, 1);
        sfx('sfx_stalker_sting', { gain: 0.28, cooldownMs: 3000 });
        this.showCaption(`A weapon readied, ${this.octantLabel(this.hx, this.hz)}`);
        this.raiseEvidence(EVIDENCE_CONFIRMED, 'the commit tell');
      }
      if (phase === 'withdraw' && h.materialized && b.state === 'stalking' && !this.escape && this.escapedRevision !== h.revision) {
        const d = Math.hypot(this.hx - px, this.hz - pz);
        if (d < ESCAPE_NEAR_M) { this.escapedRevision = h.revision; this.beginEscape(conn, b, px, pz); }
      }
      this.lastRevision = h.revision; this.lastPhase = phase;
    }

    this.recognize(dt);
    const d = Math.hypot(this.hx - px, this.hz - pz);
    const drone = phase === 'engage' ? 0.35 : phase === 'commit' ? 0.25
      : this.evidence >= EVIDENCE_CORROBORATED && d < 140 ? 0.12 : 0;
    this.setDrone(drone);
    // THE SCORE MAY KNOW ONLY WHAT YOU KNOW: the combat music hears of the hunter at `commit` / `engage` — phases the
    // game itself announces with a clear tell — never while it merely stalks (a soundtrack must not out a hidden actor).
    if (phase === 'engage' || phase === 'commit') reportThreat('hunter', performance.now());
    this.syncRing(b);
  }

  /** The recognition pipeline: distance → frustum → projected size → occlusion → clear-view dwell. */
  private recognize(dt: number): void {
    if (!this.body) return;
    const px = atlas.localX, pz = atlas.localZ;
    const d = Math.hypot(this.hx - px, this.hz - pz);
    let clear = false;
    if (d <= 180) {
      const vfov = (globalThis as { __cameraFov?: () => number }).__cameraFov?.() ?? 70;
      const aspect = window.innerWidth / Math.max(1, window.innerHeight);
      const hfov = 2 * Math.atan(Math.tan((vfov * Math.PI) / 360) * aspect);
      let rel = Math.atan2(this.hx - px, this.hz - pz) - forwardBearing(atlas.localYaw);
      while (rel > Math.PI) rel -= Math.PI * 2;
      while (rel < -Math.PI) rel += Math.PI * 2;
      const inFrustum = Math.abs(rel) <= hfov / 2 + 0.05;
      const px_h = projectedHeightPx(d, window.innerHeight, vfov);
      if (inFrustum && px_h >= RECOGNIZE_MIN_PX) {
        if (++this.occlusionFrame % OCCLUSION_EVERY === 0) this.occluded = this.lineOccluded(px, atlas.localY + 1.6, pz, this.hx, this.hy + 1.4, this.hz);
        clear = !this.occluded;
      }
    }
    if (clear) this.clearS += dt;
    else this.clearS = Math.max(0, this.clearS - dt * 2); // brief occlusion is forgiven; looking away forgets slowly
    if (this.clearS >= recognitionNeededS(d)) {
      if (this.evidence < EVIDENCE_CORROBORATED) this.raiseEvidence(EVIDENCE_CORROBORATED, 'a figure, recognised');
      if (d <= IDENTIFY_RANGE_M) this.raiseEvidence(EVIDENCE_CONFIRMED, 'a clear look, up close');
    }
  }

  /** Buildings between two points (the solids registry) or a terrain ridge — a few samples, not a raycast. */
  private lineOccluded(ax: number, ay: number, az: number, bx: number, by: number, bz: number): boolean {
    const conn = atlas.conn;
    const n = 5;
    for (let i = 1; i < n; i++) {
      const t = i / n;
      const x = ax + (bx - ax) * t, y = ay + (by - ay) * t, z = az + (bz - az) * t;
      if (structureHitAt(x, z, y)) return true;
      if (conn && groundYIn(conn, atlas.currentWorldId, x, z) > y + 0.3) return true;
    }
    return false;
  }

  /** Evidence only ever rises inside a contract; it resets when the contract closes. */
  private raiseEvidence(level: number, why: string): void {
    if (level <= this.evidence) return;
    this.evidence = level;
    if (level >= EVIDENCE_CONFIRMED && !this.identified) {
      this.identified = true;
      sfx('sfx_stalker_sting', { gain: 0.45, cooldownMs: 12_000 });
      const b = this.contract();
      if (b) this.showCaption(`${b.hunterName}. ${why}.`);
    }
  }

  // ── Cues: the evidence stream ───────────────────────────────────────────
  private surfaceCues(conn: NonNullable<typeof atlas.conn>): void {
    try {
      const rows = (conn.db as unknown as { bountyCue?: { iter(): Iterable<CueRow> } }).bountyCue;
      if (!rows) return;
      const nowMicros = BigInt(Math.round(Date.now() * 1000));
      for (const c of rows.iter()) {
        const key = c.cueId.toString();
        if (this.seenCues.has(key)) continue;
        this.seenCues.add(key);
        if (this.firstCueScan) continue; // never replay old evidence as live evidence
        if (!c.target.isEqual(conn.identity)) continue;
        if (nowMicros > c.expiresAt.microsSinceUnixEpoch) continue;
        if (nowMicros - c.createdAt.microsSinceUnixEpoch > 20n * 1_000_000n) continue; // a late delivery is not a fresh sound
        this.playCue(conn, c);
      }
      this.firstCueScan = false;
      if (this.seenCues.size > 400) this.seenCues = new Set([...this.seenCues].slice(-200));
    } catch { /* not subscribed yet */ }
  }

  private playCue(conn: NonNullable<typeof atlas.conn>, c: CueRow): void {
    if (atlas.spaceFlight) return;
    const kind = c.kind as CueKind;
    const d = Math.hypot(c.x - atlas.localX, c.z - atlas.localZ);
    const dir = this.octantLabel(c.x, c.z);
    const near = Math.max(0.05, Math.min(1, 1 - d / 90));
    // THE CUE IS HEARD WHERE THE ROW SAYS IT WAS (2026-09-20). Evidence used to be centred mono with the direction only
    // in the caption; now the sound itself comes from the cue's position. That position is the SERVER's blurred one
    // (CUE_BLUR_M per channel), so the panner leaks exactly what the row and the caption already did — and the one
    // cue whose caption names no direction (the radio) stays un-located on purpose.
    const at = { x: c.x, y: atlas.localY + 1, z: c.z };
    switch (kind) {
      case 'footstep':
        if (sfxAvailable('sfx_hunter_step') === true) {
          // The recorded cue IS two careful steps a second apart — played once, at its own pitch. Behind it, faintly, the
          // gear of a body that moved: same place, same cue, so it leaks nothing the footsteps did not.
          this.playCueSound('sfx_hunter_step', 'sfx_land', 0.14 + 0.26 * near, 1, at);
          if (sfxAvailable('sfx_hunter_harness') === true) sfxLater(140, () => { if (this.contract()) this.playCueSound('sfx_hunter_harness', 'sfx_land', 0.05 + 0.12 * near, 1, at); });
        } else {
          this.playCueSound('sfx_hunter_step', 'sfx_land', 0.1 + 0.22 * near, 0.85, at);
          // The second footfall dies with the world it was scheduled in (a plain setTimeout could step into the next one).
          sfxLater(380, () => { if (this.contract()) this.playCueSound('sfx_hunter_step', 'sfx_land', 0.08 + 0.2 * near, 0.9, at); });
        }
        this.showCaption(`Footsteps, ${dir}`);
        break;
      case 'silhouette':
        return; // the body itself is the cue; recognition decides what it means
      case 'radio':
        this.playCueSound('sfx_hunter_radio', 'sfx_button_press', sfxAvailable('sfx_hunter_radio') === true ? 0.22 : 0.16, sfxAvailable('sfx_hunter_radio') === true ? 1 : 0.7);
        this.showCaption('A carrier opens and shuts');
        break;
      case 'witness': {
        const line = WITNESS_LINES[c.variant % WITNESS_LINES.length];
        let who = 'a passer-by';
        try { const n = nameFromTables(conn, c.refId); if (n && !/^#|^versona/.test(n)) who = n; } catch { /* fine */ }
        this.showCaption(`“${line}” — ${who}`, 6000);
        break;
      }
      case 'birds':
        sfxAt('sfx_bird_flutter', { ...at, y: at.y + 5 }, { gain: 0.12 + 0.2 * near, cooldownMs: 1500, emitter: 'cue' });
        this.showCaption(`Birds startle, ${dir}`);
        break;
      case 'door':
        sfxAt('sfx_door_close', at, { gain: 0.08 + 0.18 * near, cooldownMs: 1500, emitter: 'cue' });
        this.showCaption(`A door settles, ${dir}`);
        break;
      case 'grit':
        sfxAt('sfx_land', { ...at, y: at.y + 6 }, { gain: 0.1, rate: 1.3, cooldownMs: 1500, emitter: 'cue' }); // "above" — an HRTF voice can say so
        this.showCaption('Grit falls, above');
        break;
      default:
        return;
    }
    this.cueLog.push({ kind, atS: this.clockS });
    if (this.cueLog.length > 24) this.cueLog.shift();
    // Two INDEPENDENT cues (different kinds) inside the window corroborate; one is suspicion.
    const recent = this.cueLog.filter((e) => this.clockS - e.atS <= CORROBORATE_WINDOW_S);
    const kinds = new Set(recent.map((e) => e.kind));
    this.raiseEvidence(kinds.size >= 2 ? EVIDENCE_CORROBORATED : EVIDENCE_SUSPICIOUS, kinds.size >= 2 ? 'two things that do not add up' : 'something to check');
  }

  /** A bespoke cue clip when it is installed, the fallback clip until then. */
  private playCueSound(key: string, fallback: string, gain: number, rate: number, at?: { x: number; y: number; z: number }): void {
    const have = sfxAvailable(key);
    const clip = have === true ? key : fallback;
    // A stand-in keeps the hunter's WORTH: `sfx_land` as a footstep is evidence (npcFoley, 78), not your own landing thud.
    if (at) sfxAt(clip, at, { gain, rate, cooldownMs: 250, emitter: 'cue', bus: 'npcFoley', priority: 78 });
    else sfx(clip, { gain, rate, cooldownMs: 250 });
  }

  /** "behind left", "ahead", "to the right" — relative to the mark's facing (the facing law). */
  private octantLabel(x: number, z: number): string {
    let rel = Math.atan2(x - atlas.localX, z - atlas.localZ) - forwardBearing(atlas.localYaw);
    while (rel > Math.PI) rel -= Math.PI * 2;
    while (rel < -Math.PI) rel += Math.PI * 2;
    const a = Math.abs(rel);
    // three's yaw turns the facing clockwise seen from above: a positive bearing offset is to the LEFT.
    const side = rel > 0 ? 'left' : 'right';
    if (a < Math.PI / 8) return 'ahead';
    if (a < 3 * Math.PI / 8) return `ahead ${side}`;
    if (a < 5 * Math.PI / 8) return `to the ${side}`;
    if (a < 7 * Math.PI / 8) return `behind ${side}`;
    return 'behind';
  }

  // ── The shelter report (bounded trust: a TTL the server enforces) ──────
  private reportShelter(conn: NonNullable<typeof atlas.conn>, dt: number): void {
    const inside = !!atlas.interiorId;
    const r = conn.reducers as unknown as { bountyReportShelter?: (a: { inside: boolean }) => Promise<void> };
    if (typeof r.bountyReportShelter !== 'function') return;
    this.shelterAcc += dt;
    if (inside && (!this.shelterWas || this.shelterAcc >= SHELTER_REPORT_S)) {
      this.shelterAcc = 0;
      void r.bountyReportShelter({ inside: true }).catch(() => { /* not in a town */ });
    } else if (!inside && this.shelterWas) {
      void r.bountyReportShelter({ inside: false }).catch(() => { /* fine */ });
    }
    this.shelterWas = inside;
  }

  // ── The telegraph (the server's windup row IS the tell) ────────────────
  private ensureStrikeHooks(conn: NonNullable<typeof atlas.conn>): void {
    if (this.strikeSubbed) return;
    const db = conn.db as unknown as { npcStrike?: { onInsert(cb: (ctx: unknown, row: StrikeRow) => void): void } };
    if (!db.npcStrike) return;
    this.strikeSubbed = true;
    db.npcStrike.onInsert((_c, row) => {
      if (!row.attackerKey.startsWith('hunter:') || !conn.identity || !row.targetPlayer.isEqual(conn.identity)) return;
      sfx('sfx_hunter_strike', { gain: 0.5, cooldownMs: 600 });
      this.raiseEvidence(EVIDENCE_CONFIRMED, 'the blow');
    });
  }

  private syncRing(b: BountyRow): void {
    if (!this.body) return;
    const w = windups.get(`hunter:${b.bountyId}`);
    const t = w ? Math.min(1, (performance.now() - w.start) / Math.max(1, w.ms)) : -1;
    if (t < 0) { if (this.ring) { this.body.remove(this.ring); this.ring = null; } return; }
    if (!this.ringGeo) this.ringGeo = new THREE.RingGeometry(0.72, 1, 40);
    if (!this.ringMat) { this.ringMat = new THREE.MeshBasicMaterial({ color: 0xff7a3c, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide }); markMrtOverlay(this.ringMat); }
    if (!this.ring) {
      this.ring = new THREE.Mesh(this.ringGeo, this.ringMat);
      this.ring.rotation.x = -Math.PI / 2;
      this.ring.position.y = 0.06;
      this.ring.renderOrder = 5;
      this.body.add(this.ring);
    }
    const s = HIT_RANGE_M * (0.3 + 0.7 * t);
    this.ring.scale.set(s, s, 1);
    this.ringMat.opacity = 0.25 + 0.45 * t;
  }

  // ── The escape: run to the ship, board, lift off ────────────────────────
  private beginEscape(conn: NonNullable<typeof atlas.conn>, b: BountyRow, px: number, pz: number): void {
    const spot = escapeShipSpot(px, pz, this.hx, this.hz);
    const sy = groundYIn(conn, atlas.currentWorldId, spot.x, spot.z);
    this.dropShip();
    this.spawnShip(b, { x: spot.x, y: sy + 1.6, z: spot.z, yaw: spot.yaw });
    this.escape = { phase: 'run', t: 0, sx: spot.x, sy: sy + 1.6, sz: spot.z, yaw: spot.yaw, hatchX: spot.hatchX, hatchZ: spot.hatchZ, awayX: spot.awayX, awayZ: spot.awayZ };
    this.lastEscape = { phases: ['run'], hunterRunM: 0, climbM: 0, recedeM: 0, flightS: 0, endedBy: '' };
    this.escapeStart = { hx: this.hx, hz: this.hz, sy: sy + 1.6, d: Math.hypot(spot.x - px, spot.z - pz) };
    this.vanishUntil = this.clockS + ESCAPE_FLIGHT_S + 60 + this.hash01(`${b.bountyId}|flee|${this.escapes}`) * 90;
    this.escapes++;
    if (this.identified) sfx('sfx_stalker_sting', { gain: 0.4, cooldownMs: 4000 });
    try { this.shipLoop(spot.x, sy + 1.6, spot.z).set(0.22, 0.9, 0.6); } catch { /* audio locked */ }
    this.showCaption(`A figure breaks for a ship, ${this.octantLabel(this.hx, this.hz)}`);
    this.raiseEvidence(EVIDENCE_CORROBORATED, 'a figure that ran');
  }

  private escapeFrame(conn: NonNullable<typeof atlas.conn>, b: BountyRow, dt0: number): void {
    const e = this.escape;
    if (!e || !this.ship) { this.endEscape('no-ship'); return; }
    const dt = dt0 * this.escapeTimeScale;
    const px = atlas.localX, pz = atlas.localZ;
    e.t += dt;
    if (e.phase === 'run') {
      if (!this.body && !this.showBody()) { this.endEscape('no-body'); return; }
      const dx = e.hatchX - this.hx, dz = e.hatchZ - this.hz;
      const d = Math.hypot(dx, dz);
      const step = Math.min(CHASE_SPEED * ESCAPE_RUN_MULT * dt, d);
      if (d > 0.01) { this.hx += (dx / d) * step; this.hz += (dz / d) * step; }
      this.hy = groundYIn(conn, atlas.currentWorldId, this.hx, this.hz);
      this.hyaw = Math.atan2(e.hatchX - this.hx, e.hatchZ - this.hz);
      this.poseBody(b, 'withdraw', 'breaking for its ship');
      this.setDrone(this.identified ? 0.3 : 0.1);
      if (this.lastEscape) this.lastEscape.hunterRunM = Math.hypot(this.hx - this.escapeStart.hx, this.hz - this.escapeStart.hz);
      if (d <= ESCAPE_BOARD_M) { e.phase = 'board'; e.t = 0; this.hideBody(); this.lastEscape?.phases.push('board'); }
      return;
    }
    if (e.phase === 'board') {
      if (e.t >= ESCAPE_BOARD_S) {
        e.phase = 'lift'; e.t = 0;
        this.lastEscape?.phases.push('lift');
        sfxAt('sfx_ship_takeoff', { x: e.sx, y: e.sy, z: e.sz }, { gain: 0.5, cooldownMs: 2000, emitter: 'hunter-ship' });
        try { this.shipLoop(e.sx, e.sy, e.sz).set(0.4, 1.1, 0.4); } catch { /* audio locked */ }
      }
      return;
    }
    const f = escapeFlight(e.t);
    e.sy += f.riseMps * dt;
    e.sx += e.awayX * f.forwardMps * dt;
    e.sz += e.awayZ * f.forwardMps * dt;
    const minY = groundYIn(conn, atlas.currentWorldId, e.sx, e.sz) + 1.6;
    if (e.sy < minY) e.sy = minY;
    this.ship.position.set(e.sx, e.sy, e.sz);
    this.ship.rotation.set(f.pitch, e.yaw, f.bank);
    const d = Math.hypot(e.sx - px, e.sz - pz);
    if (this.lastEscape) { this.lastEscape.climbM = e.sy - this.escapeStart.sy; this.lastEscape.recedeM = d - this.escapeStart.d; this.lastEscape.flightS = e.t; }
    try { this.shipLoop(e.sx, e.sy, e.sz).set(Math.max(0, 0.4 * (1 - d / 900)), 1.1, 0.3); } catch { /* audio locked */ }
    if (e.t >= ESCAPE_FLIGHT_S || d > 900) { if (this.lastEscape) this.lastEscape.endedBy = 'flown'; this.endEscape(); }
  }

  private endEscape(why = 'interrupt'): void {
    if (this.lastEscape && !this.lastEscape.endedBy) this.lastEscape.endedBy = why;
    this.escape = null;
    this.escapeTimeScale = 1;
    this.hideBody();
    this.dropShip();
    try { sfxLoop('sfx_ship_engine', 'hunter').set(0, 1, 0.5); } catch { /* audio locked */ }
  }

  /**
   * The escape ship's engine: ITS OWN loop of the shared clip (scope `hunter`), heard where the hull is. It used to be
   * the SAME loop as your own ship's engine — one source per clip key, "the last set() wins" — kept apart only by the
   * hope that you were never flying while it fled.
   */
  private shipLoop(x: number, y: number, z: number): ReturnType<typeof sfxLoop> {
    const l = sfxLoop('sfx_ship_engine', 'hunter');
    l.at({ x, y, z });
    return l;
  }

  // ── The air duel (legacy: the client's hull; the server suspends the ground agent aloft) ──
  private airFrame(conn: NonNullable<typeof atlas.conn>, b: BountyRow, dt: number): void {
    const t = this.tierOf(b);
    const px = atlas.localX, pz = atlas.localZ;
    this.hideBody();
    if (!this.ship) this.spawnShip(b);
    if (!this.ship) return;
    const py = atlas.localY;
    const dx = px - this.hx, dy = py + 6 - this.hy, dz = pz - this.hz;
    const d = Math.hypot(dx, dz);
    const keep = 190;
    if (d > keep) {
      const step = Math.min(AIR_CHASE_SPEED * dt, d - keep * 0.9);
      this.hx += (dx / Math.max(d, 1)) * step;
      this.hz += (dz / Math.max(d, 1)) * step;
    }
    this.hy += Math.max(-40 * dt, Math.min(40 * dt, dy));
    const minY = groundYIn(conn, atlas.currentWorldId, this.hx, this.hz) + 14;
    if (this.hy < minY) this.hy = minY;
    this.ship.position.set(this.hx, this.hy, this.hz);
    this.ship.rotation.y = Math.atan2(dx, dz);
    this.raiseEvidence(EVIDENCE_CONFIRMED, 'a hull on your tail');

    const id = `bounty:h:${b.bountyId}`;
    const scaled = Math.round(b.hunterHp * 1.6);
    const tgt = airTargetById(id);
    if (!tgt) {
      upsertAirTarget({ id, battleId: b.bountyId, side: 'a', kind: 'hunter', x: this.hx, y: this.hy, z: this.hz, r: 9, hp: scaled, hitByPlayer: false, obj: this.ship });
      this.lastPushedHp = scaled;
    } else {
      tgt.x = this.hx; tgt.y = this.hy; tgt.z = this.hz;
      this.shootCd -= dt;
      if (tgt.hp < this.lastPushedHp && this.shootCd <= 0) {
        this.shootCd = SHOOT_CD_S;
        void conn.reducers.shootBountyHunter({ bountyId: b.bountyId }).catch(() => { /* cycling */ });
      }
      tgt.hp = scaled; // the server is the health authority
      this.lastPushedHp = scaled;
    }
    this.hitAcc += dt;
    if (this.hitAcc >= AIR_FIRE_GAP_S && d < AIR_FIRE_RANGE_M) {
      this.hitAcc = 0;
      bus.emit('atlas:ship-damage', { amount: Math.max(3, Math.round(t.damage * 0.55)), why: b.hunterName });
      sfx('sfx_hunter_strike', { gain: 0.3, cooldownMs: 900 });
    }
    this.setDrone(0.4);
  }

  // ── The local prediction of the score (diagnostics; the server decides) ─
  private predictWeakness(conn: NonNullable<typeof atlas.conn>, b: BountyRow, world: WorldRow | null): void {
    if (b.state !== 'stalking') return;
    const me = conn.identity;
    const db = conn.db as unknown as {
      playerVitals: { identity: { find(i: unknown): VitalsRow | undefined } };
      creature: { iter(): Iterable<CreatureRow> };
      playerEquipment: { iter(): Iterable<EquipRow> };
      skyState: { worldId: { find(id: bigint): SkyRow | undefined } };
    };
    const v = (() => { try { return db.playerVitals.identity.find(me); } catch { return undefined; } })();
    const nowMicros = BigInt(Math.round(Date.now() * 1000));
    const hurtAgoS = v ? Number(nowMicros - v.lastHurtAt.microsSinceUnixEpoch) / 1e6 : 1e9;
    let creatureOnMe = false;
    try { for (const c of db.creature.iter()) { if (c.state === 'aggro' && c.targetKind === 'player' && c.targetPlayer.isEqual(me)) { creatureOnMe = true; break; } } } catch { /* fine */ }
    let armed = false;
    try { for (const eq of db.playerEquipment.iter()) { if (eq.slot === 'weapon' && eq.owner.isEqual(me)) { armed = true; break; } } } catch { /* fine */ }
    let near = 0;
    for (const r of atlas.remotes.values()) if (Math.hypot(r.targetX - atlas.localX, r.targetZ - atlas.localZ) < 200) near++;
    let distSafety = Math.hypot(atlas.localX, atlas.localZ);
    if (world?.kind === 'commons') {
      distSafety = Infinity;
      for (const s of settlementSites(world.terrainSeed)) distSafety = Math.min(distSafety, Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ));
    }
    let night = false;
    try { const sky = atlas.currentWorldId !== null ? db.skyState.worldId.find(atlas.currentWorldId) : undefined; if (sky) night = sunElevation(sky.timeOfDay) < 0; } catch { /* fine */ }
    const m: WeaknessMetrics = {
      healthFrac: v ? v.hp / Math.max(1, v.maxHp) : 1,
      distToSafetyM: Number.isFinite(distSafety) ? distSafety : 900,
      playersNearby: near, night,
      recentlyHurt: hurtAgoS < 60 || this.clockS - this.lastShipDamageAt < 60,
      inOtherCombat: creatureOnMe || atlas.combatSpace,
      onFoot: !atlas.hoverRide && !atlas.skyFlight && !atlas.spaceFlight,
      speedMps: this.speedMps, unarmed: !armed, deepSpace: false,
    };
    this.lastScore = weaknessScore(m);
  }

  // ── Server-authored events → toasts ────────────────────────────────────
  private surfaceEvents(conn: NonNullable<typeof atlas.conn>): void {
    try {
      const rows = (conn.db as unknown as { bountyEvent: { iter(): Iterable<EventRow> } }).bountyEvent.iter();
      const nowMicros = BigInt(Math.round(Date.now() * 1000));
      for (const e of rows) {
        const key = e.eventId.toString();
        if (this.seenEvents.has(key)) continue;
        this.seenEvents.add(key);
        if (this.firstEventScan) continue; // never replay the backlog as live dread
        if (!e.target.isEqual(conn.identity)) continue;
        if (nowMicros - e.at.microsSinceUnixEpoch > 300n * 1_000_000n) continue;
        if (e.kind === 'whisper') {
          bus.emit('atlas:status', { text: `… ${e.detail}` });
          sfx('sfx_stalker_sting', { gain: 0.25, cooldownMs: 8000 });
          this.cueLog.push({ kind: 'whisper', atS: this.clockS });
          this.raiseEvidence(EVIDENCE_SUSPICIOUS, 'a whisper');
        } else if (e.kind === 'strike') {
          bus.emit('atlas:status', { text: `☠ ${e.detail}` });
          sfx('sfx_hunter_strike', { gain: 0.6, cooldownMs: 1000 });
          this.raiseEvidence(EVIDENCE_CONFIRMED, 'it came for you');
        } else if (e.kind === 'slain') {
          bus.emit('atlas:status', { text: e.detail });
        } else if (e.kind === 'fulfilled') {
          bus.emit('atlas:status', { text: `☠ ${e.detail}` });
        }
        // 'hired' stays SILENT — the mark learns from the evidence, not a receipt. 'wound' stays in the ledger.
      }
      this.firstEventScan = false;
    } catch { /* not subscribed yet */ }
  }

  // ── Visual helpers ──────────────────────────────────────────────────────
  private ensureMasters(conn: NonNullable<typeof atlas.conn>): void {
    if (this.loadTried) return;
    const bodyId = prefabAssetId(conn, 'avatar:player_duskranger') ?? prefabAssetId(conn, 'avatar:player_nightcourier');
    const shipId = prefabAssetId(conn, 'prop:ship_pirate') ?? prefabAssetId(conn, 'prop:ship_fighter');
    if (bodyId === null && shipId === null) return;
    this.loadTried = true;
    if (bodyId !== null) void this.assets.load(bodyId).then((m) => { markShared(m); this.bodyMaster = m; }).catch(() => { this.bodyMaster = null; });
    if (shipId !== null) void this.assets.load(shipId).then((m) => { markShared(m); this.shipMaster = m; }).catch(() => { this.shipMaster = null; });
  }

  private showBody(): boolean {
    if (this.body) return true;
    const scene = this.app?.world.scene;
    if (!scene) return false;
    const g = new THREE.Group();
    if (this.bodyMaster) {
      const c = this.bodyMaster.clone();
      const bb = new THREE.Box3().setFromObject(c);
      const s = 1.92 / Math.max(0.01, bb.max.y - bb.min.y);
      c.scale.setScalar(s);
      c.traverse((o) => {
        o.userData.sharedAsset = true;
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh && mesh.material) {
          // Weather cover, not a shadow: enough contrast to read a PERSON (cloned materials, never the cache).
          const src = mesh.material as THREE.MeshStandardMaterial;
          const dark = src.clone();
          dark.userData = {}; // Material.copy deep-copies userData (the shared flag would skip disposal)
          dark.color?.multiplyScalar(0.55);
          mesh.material = dark;
          mesh.userData.ownMaterial = true;
        }
      });
      g.add(c);
    } else {
      const c = new THREE.Mesh(new THREE.CapsuleGeometry(0.34, 1.2, 4, 8), new THREE.MeshStandardMaterial({ color: 0x23242c, roughness: 0.9 }));
      c.position.y = 0.95;
      g.add(c);
    }
    // The visor light: shown only once the hunter is IDENTIFIED — never a beacon on an unknown figure.
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 8), new THREE.MeshBasicMaterial({ color: 0xff2a20, toneMapped: false }));
    eye.position.set(0.07, 1.68, 0.16);
    eye.visible = false;
    g.add(eye);
    this.eye = eye;
    this.plate = makePlate();
    this.plate.sprite.position.y = 2.25;
    this.plate.sprite.visible = false;
    g.add(this.plate.sprite);
    scene.add(g);
    this.body = g;
    return true;
  }

  private poseBody(b: BountyRow, phase: HuntPhase, sub?: string): void {
    if (!this.body) return;
    this.body.position.set(this.hx, this.hy, this.hz);
    this.body.rotation.y = this.hyaw;
    if (this.eye) this.eye.visible = this.identified;
    if (this.plate) {
      this.plate.sprite.visible = this.identified;
      if (this.identified) {
        const t = this.tierOf(b);
        const fighting = phase === 'engage' || phase === 'commit';
        this.plate.draw({
          name: b.hunterName, sub: sub ?? (fighting ? t.name : phase === 'search' ? 'searching' : 'watching'),
          hp: Math.max(0, b.hunterHp / Math.max(1, b.hunterMaxHp)) * 100, morale: 100, side: 'a',
          state: fighting ? 'engage' : 'patrol',
        });
      }
    }
    // The body is a mark for the ONE attack path (WildsModule); the server refuses a blow with no body in reach.
    upsertGroundTarget({ id: `bounty:${b.bountyId}`, kind: 'hunter', label: this.identified ? b.hunterName : 'a figure', x: this.hx, y: this.hy, z: this.hz, strike: () => this.strikeHunter(b) });
  }

  private strikeHunter(b: BountyRow): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return Promise.resolve();
    return conn.reducers.strikeBountyHunter({ bountyId: b.bountyId });
  }

  private hideBody(): void {
    for (const b of [this.contract()]) if (b) removeGroundTarget(`bounty:${b.bountyId}`);
    if (!this.body) { this.visible = false; return; }
    const scene = this.app?.world.scene;
    if (scene) scene.remove(this.body);
    this.plate?.dispose();
    if (this.ring) { this.body.remove(this.ring); this.ring = null; }
    deferDisposeTree(this.body);
    this.body = null;
    this.eye = null;
    this.plate = null;
    this.visible = false;
    this.clearS = 0;
    this.setDrone(0);
  }

  private spawnShip(b: BountyRow, at?: { x: number; y: number; z: number; yaw: number }): void {
    const scene = this.app?.world.scene;
    if (!scene) return;
    const g = new THREE.Group();
    if (this.shipMaster) {
      const c = this.shipMaster.clone();
      const bb = new THREE.Box3().setFromObject(c);
      const size = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z);
      c.scale.setScalar(13 / Math.max(0.01, size));
      c.traverse((o) => { o.userData.sharedAsset = true; });
      g.add(c);
    } else {
      const c = new THREE.Mesh(new THREE.ConeGeometry(2.4, 9, 6), new THREE.MeshStandardMaterial({ color: 0x1a1a22, roughness: 0.6 }));
      c.rotation.x = Math.PI / 2;
      g.add(c);
    }
    if (at) { g.position.set(at.x, at.y, at.z); g.rotation.y = at.yaw; }
    else { this.hx = atlas.localX + 400; this.hz = atlas.localZ + 120; this.hy = atlas.localY + 60; g.position.set(this.hx, this.hy, this.hz); }
    scene.add(g);
    this.ship = g;
    this.lastPushedHp = Math.round(b.hunterHp * 1.6);
  }

  private dropShip(): void {
    if (!this.ship) return;
    const scene = this.app?.world.scene;
    if (scene) scene.remove(this.ship);
    deferDisposeTree(this.ship);
    this.ship = null;
    clearAirTargetsByPrefix('bounty:');
  }

  private teardownHunter(): void {
    if (this.escape) this.endEscape('teardown');
    this.hideBody();
    this.dropShip();
  }

  // ── Dread furniture ─────────────────────────────────────────────────────
  private setDrone(gain: number): void {
    const want = gain > 0;
    if (want === this.droneOn && !want) return;
    if (want && Math.abs(gain - this.droneGain) < 0.01 && this.droneOn) return;
    this.droneOn = want;
    this.droneGain = gain;
    try { sfxLoop('sfx_stalker_drone', 'hunter').set(gain, 1, 1.6); } catch { /* audio locked */ }
  }

  private updateHud(b: BountyRow): void {
    if (this.evidence >= EVIDENCE_CONFIRMED && b.state === 'attacking') this.setHud(`☠ MARKED — ${b.hunterName}`, true);
    else if (this.evidence >= EVIDENCE_CONFIRMED) this.setHud('you are being watched', false);
    else if (this.evidence >= EVIDENCE_CORROBORATED) this.setHud('possibly followed', false);
    else this.setHud(null);
  }

  private hudText: string | null = null;
  private hudAttacking = false;
  private setHud(text: string | null, attacking = false): void {
    if (!text) {
      if (this.hud) { this.hud.remove(); this.hud = null; this.hudText = null; }
      return;
    }
    if (!this.hud) {
      this.hud = document.createElement('div');
      this.hud.id = 'bounty-dread';
      document.body.appendChild(this.hud);
      this.hudText = null;
    }
    if (text === this.hudText && attacking === this.hudAttacking) return;
    this.hudText = text;
    this.hudAttacking = attacking;
    this.hud.textContent = text;
    this.hud.style.cssText = `position:fixed;right:14px;bottom:96px;z-index:44;pointer-events:none;`
      + `font:${attacking ? '12px' : '10px'} ui-monospace,monospace;letter-spacing:2px;`
      + `color:${attacking ? '#ff5a4a' : 'rgba(200,205,220,0.5)'};text-shadow:0 0 8px rgba(0,0,0,0.8);`
      + (attacking ? '' : 'font-style:italic;');
  }

  /** A quiet caption that describes the EVIDENCE ("Footsteps, behind left"), never the answer. */
  private showCaption(text: string, ms = CAPTION_MS): void {
    if (!this.caption) {
      this.caption = document.createElement('div');
      this.caption.id = 'bounty-caption';
      this.caption.style.cssText = 'position:fixed;right:14px;bottom:114px;z-index:44;pointer-events:none;'
        + 'font:11px ui-monospace,monospace;letter-spacing:1px;color:rgba(220,224,236,0.72);text-shadow:0 0 8px rgba(0,0,0,0.85);'
        + 'font-style:italic;transition:opacity 0.6s;opacity:1';
      document.body.appendChild(this.caption);
    }
    this.caption.textContent = text;
    this.caption.style.opacity = '1';
    this.captionUntil = performance.now() + ms;
  }
  private tickCaption(): void {
    if (!this.caption) return;
    if (performance.now() > this.captionUntil && this.caption.style.opacity !== '0') this.caption.style.opacity = '0';
  }

  private hash01(s: string): number {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    h ^= h << 13; h >>>= 0; h ^= h >> 17; h ^= h << 5; h >>>= 0;
    return (h % 10_000) / 10_000;
  }

  dispose(): void {
    this.teardownHunter();
    this.setHud(null);
    this.caption?.remove(); this.caption = null;
    this.setDrone(0);
  }
}
